import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { selectProduct, setVizLoading } from '../../../../store/visualizerSlice';
import Product from '../../Product/Product';
import StepNavigation from '../../StepNavigation/StepNavigation';
import styles from './ShowerFloor.module.css';

// Helper function to generate tier label dynamically (T1 -> Tier 1, T10 -> Tier 10)
const getTierLabel = (tier) => {
    if (!tier) return tier;
    const match = tier.match(/^T(\d+)$/);
    if (match) {
        return `Tier ${match[1]}`;
    }
    return tier;
};

export default function ShowerFloor({ 
    surfaceName = 'Shower Floor', 
    stepIndex,
    room = '',
    onViewAllApplied, 
    onNext, 
    onPrevious 
}) {
    const dispatch = useDispatch();
    const stepSelections = useSelector(state => state.visualizer.stepSelections);
    const [productData, setProductData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedProductId, setSelectedProductId] = useState(null);

    // Selection key: StepName_Room (e.g., ShowerFloor_Bathroom)
    const selectionKey = `ShowerFloor_${room}`;

    // Restore selection from Redux on mount
    useEffect(() => {
        const savedSelection = stepSelections[selectionKey];
        if (savedSelection?.product?.id) {
            setSelectedProductId(savedSelection.product.id);
        }
    }, [stepSelections, selectionKey]);

    // Fetch product data
    useEffect(() => {
        setLoading(true);
        setError(null);

        const URL = `https://lizard.chameleonpower.com/CDN/ProductsBySeries/9718.json`;
        
        fetch(URL)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch products: ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                // Filter products by descriptor == "Shower Floor"
                const filtered = (Array.isArray(data) ? data : []).filter(
                    p => p.descriptor === 'Shower Floor'
                );
                setProductData(filtered);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching products:', error);
                setError(error.message);
                setLoading(false);
            });
    }, []);

    // Group products by tier (style attribute)
    // Products without tier go to "Included" group at the top
    const groupedProducts = (() => {
        const groups = {};
        
        // First, add products without tier (Included group)
        const includedProducts = productData.filter(p => !p.style);
        if (includedProducts.length > 0) {
            groups['Included'] = includedProducts;
        }
        
        // Get all unique tiers and sort them (T1, T2, T3, ... T10+)
        const tiers = [...new Set(productData.map(p => p.style).filter(Boolean))];
        const sortedTiers = tiers.sort((a, b) => {
            const numA = parseInt(a.replace('T', '')) || 0;
            const numB = parseInt(b.replace('T', '')) || 0;
            return numA - numB;
        });
        
        // Add tiered products
        sortedTiers.forEach(tier => {
            const tierProducts = productData.filter(p => p.style === tier);
            if (tierProducts.length > 0) {
                groups[tier] = tierProducts;
            }
        });
        
        return groups;
    })();

    const handleProductSelect = (product) => {
        setSelectedProductId(product.id);
        
        console.log('ShowerFloor - Product selected:', product.color || product.name);
        console.log('ShowerFloor - Applying to surface:', surfaceName);
        
        dispatch(selectProduct({
            product,
            surfaceName,
            stepIndex,
            selectionKey
        }));
        
        const chamViz = document.getElementById('chamViz');
        if (!chamViz) {
            console.error('ShowerFloor - chamViz element not found');
            return;
        }

        if (!product.id) {
            console.error('ShowerFloor - Product has no ID');
            return;
        }

        console.log('ShowerFloor - Applying product ID:', product.id);
        dispatch(setVizLoading(true));
        
        // Apply with pattern: product_id(3262177)
        const selString = `${product.id}(3262177)`;
        
        chamViz.applyByFilter(
            selString,
            "",
            function(newSel) {
                if (this.name === surfaceName) {
                    return selString;
                }
                return this.sel;
            },
            true
        );
    };

    const handleViewAllApplied = () => {
        if (onViewAllApplied) {
            onViewAllApplied();
        }
    };

    return (
        <div className={styles.container}>
            <StepNavigation
                title="Shower Floor"
                onNext={onNext}
                onPrevious={onPrevious}
                onViewAllApplied={handleViewAllApplied}
            />

            <div className={styles.productListContainer}>
                {loading && (
                    <div className={styles.message}>Loading products...</div>
                )}

                {error && (
                    <div className={styles.errorMessage}>Error: {error}</div>
                )}

                {!loading && !error && Object.keys(groupedProducts).length === 0 && (
                    <div className={styles.message}>No products available.</div>
                )}

                {!loading && !error && Object.entries(groupedProducts).map(([tier, products]) => (
                    <div key={tier} className={styles.tierSection}>
                        <div className={styles.tierHeader}>
                            <span className={styles.tierTitle}>
                                {tier === 'Included' ? 'Included' : getTierLabel(tier)}
                            </span>
                            <div className={styles.tierLine}></div>
                        </div>
                        <ul className={styles.productGrid}>
                            {products.map(product => (
                                <Product
                                    key={product.id}
                                    id={product.id}
                                    specifier={product.specifier}
                                    focus={500}
                                    size="256"
                                    title={product.color || ''}
                                    description={product.name || ''}
                                    series="9718"
                                    onSelect={(id) => {
                                        const selected = products.find(p => p.id === id);
                                        if (selected) handleProductSelect(selected);
                                    }}
                                    isActive={product.id === selectedProductId}
                                />
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    );
}

ShowerFloor.propTypes = {
    surfaceName: PropTypes.string,
    stepIndex: PropTypes.number.isRequired,
    room: PropTypes.string,
    onViewAllApplied: PropTypes.func,
    onNext: PropTypes.func,
    onPrevious: PropTypes.func
};
